import { ReactElement } from 'react'

import { LineStyle } from '@editor/interfaces/canvas'
import { DrawingStyles, DrawingType } from '@editor/interfaces/drawing'
import { CanvasConfigV2, CanvasProps, EditorConfig, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { ShapeKeyType } from '@editor/interfaces/shape'
import { UserInfo } from '@editor/interfaces/user'
import { RootState } from '@react-three/fiber'

import { BlueprintEditorContextState } from './contexts/BlueprintEditorContext'

// Conversion constant: 1 point = 0.352777 mm (1/72 inch)
export const PT_TO_MM = 0.352777

export const DEFAULT_STYLES: Partial<DrawingStyles> = {
  strokeColor: '#000000',
  strokeWidth: 1,
  strokeStyle: LineStyle.Solid,
  fillColor: 'transparent',
  textColor: '#000000',
  textSize: 18,
  textOffset: 15,
}

export enum Mode {
  Editor3D,
  Editor2D,
}

export enum BlueprintEditorTools {
  // General tools
  BlueprintManager = 'blueprint-manager',
  MaskPCD = 'mask-pcd',
  Editor3DElements = 'editor-3d-elements',

  // Drawing tools
  Selector = 'selector',
  Pen = 'pen',
  Polygon = 'polygon',
  Circle = 'circle',
  Rectangle = 'rectangle',
  TextAnnotation = 'text-annotation',
  MeasurementLabel = 'measurement-label',

  // Special tools
  Focus = 'focus',
}

export enum BlueprintToolbarCategory {
  None,
  Core,
  Draw,
  Annotation,
}

/**
 * Definition of a tool to be used by Editor.
 */
export interface BlueprintEditorTool {
  /**
   * Tool key (must be from EDITOR_TOOLS constant)
   */
  key: BlueprintEditorTools

  /**
   * What type of drawing this tool creates.
   */
  drawingType?: DrawingType

  /**
   * Check if the tool is allowed for the user.
   * This will show/hide the tool in the toolbar.
   *
   * Must be checked against WRITE permission.
   *
   * @param userProfile User's profile information.
   * @param isAllowedToModify Whether the user has modify permission on the project.
   *                          Tools that perform write operations must check this
   *                          so they stay hidden from e.g. invited users.
   * @returns
   */
  authCheck: (userProfile: UserInfo | null, isAllowedToModify: boolean) => boolean

  /**
   * Toolbar-related configurations
   */
  toolbar?: {
    /**
     * Tool icon. Must be a React component.
     * Define it first in assets/icons.tsx
     */
    icon: ReactElement

    /**
     * Tool label. Used for tooltip when hovering over the tool.
     */
    label: string

    /**
     * Tool variants configuration. Usually used for multiple tools under the same category.
     */
    variants?: BlueprintEditorTool[]

    /**
     * Tool category. Used to group tools in the toolbar.
     * Default is ToolbarCategory.None.
     */
    category?: BlueprintToolbarCategory

    /**
     * The mode this tool is used in.
     * If not provided, the tool will be available in all modes.
     *
     * On modes other than defined here, the tool icon will still appear but disabled.
     */
    modes?: Mode[]
  }

  /**
   * Tool hooks
   */
  hooks?: {
    /**
     * Hook to be executed by Editor.tsx
     * Usually used to define components to be shown on the canvas.
     * Context value is passed directly here since the hook is run _inside_ Editor.tsx
     * itself. The context has not been set yet, so useContext() will not work.
     */
    useEditor?: (contextValue: BlueprintEditorContextState) => EditorConfig

    /**
     * Hook for MainCanvas.
     * Most behavior will defined in this hook.
     *
     * @param props
     */
    useMainCanvas?: (props: CanvasProps) => CanvasConfigV2

    /**
     * Hook to be executed on each frame.
     */
    useFrame?: (props: RootState) => void

    /**
     * Hook to return items for the editor elements panel.
     * When generating items at parent-level, make sure to check if the parent is undefined
     * and only return the items at this time.
     *
     * @param parent Parent item. If the item is a child, this will be the parent item.
     *               If the item is a parent, this will be undefined.
     *               Check EditorPanelItem['item'] for allowed types, but generally
     *               it must extend LayerStatus.
     */
    useElementsPanel?: () => ElementsPanelConfig

    /**
     * Hook to return items for the drawing items panel.
     * When generating items at parent-level, make sure to check if the parent is undefined
     * and only return the items at this time.
     *
     * @param parent Parent item. If the item is a child, this will be the parent item.
     *               If the item is a parent, this will be undefined.
     *               Check EditorPanelItem['item'] for allowed types, but generally
     *               it must extend LayerStatus.
     */
    useDrawingsPanel?: () => ElementsPanelConfig
  }

  /**
   * Components to be added to various parts of the editor.
   */
  components?: {
    /**
     * Components to be added on the editor. This will be added outside of the canvas.
     * Use this to add modals, etc.
     * Must be a React component.
     */
    editor?: React.FC[]

    /**
     * Components to be added on the canvas.
     * Must be a THREE.js component.
     */
    canvas?: React.FC[]

    /**
     * Component to be added on the submission panel.
     * This panel will appear just before the submission button.
     * A tool can only have 1 panel at a time. If a tool has multiple panels for different states,
     * change the panel based on the state within the panel itself.
     */
    panel?: React.FC
  }

  /**
   * Configuration for the tool.
   */
  config?: {
    /**
     * Whether to switch to the drawing panel when the tool is active.
     */
    switchToDrawingPanelOnActive?: boolean

    /**
     * Additional toolbars to be shown.
     */
    additionalToolbars?: {
      /**
       * Whether to use cuboid controls.
       */
      cuboidControls?: boolean
    }

    /**
     * Volume configuration.
     */
    volume?: {
      /**
       * Whether the tool requires a volume to be created.
       */
      required?: boolean

      /**
       * If volume can be selected.
       */
      selectable?: boolean

      /**
       * If only one volume can be selected.
       */
      onlyOneSelectable?: boolean
    }

    cylinder?: {
      /**
       * Whether the tool requires a cylinder to be created.
       */
      required?: boolean
    }

    /**
     * Config for selecting/placing anchors.
     */
    anchor?: {
      /**
       * Whether anchor can be placed on objects.
       * By default, anchors can be placed on objects.
       */
      notPlaceableOnObjects?: boolean

      /**
       * If there's a solid mesh, it will be used to place the anchor.
       * Otherwise, it will fall back to PCD.
       */
      useSolidMesh?: boolean

      /**
       * Show the current anchor position as a circle anchor.
       * Default to not showing.
       */
      showCurrentAnchor?: boolean
    }

    /**
     * Type of shapes that can be selected by this tool.
     */
    selectableShapes?: ShapeKeyType[]

    /**
     * Inspection items that can be selected by this tool.
     */
    selectableInspectionItems?: InspectionItemType[]

    /**
     * These are tools that cannot be used at the same time with the current tool.
     * If the current tool is being saved, these tools will be disabled.
     *
     * Make sure to include itself if it should be locked when any of its
     * elements are being saved.
     */
    dependencies?: BlueprintEditorTools[]
  }
}
